import { motion } from 'framer-motion';
import { ArrowRight, Building, Globe, Users, Heart, Lightbulb, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import EspaceProNav from '../components/layout/EspaceProNav';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";
const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_organize-smart/artifacts/l6ybtiux_14069.jpg";
const TEAM_MADAGASCAR = "https://customer-assets.emergentagent.com/job_4e491698-f834-49a8-a1e5-8fd3efa88feb/artifacts/x1wh0gls_FB_IMG_1772976581762.jpg";

const achievements = [
  "Créé et géré trois clubs de remise en forme",
  "Dirigé le plus grand complexe de remise en forme d'Afrique et le second du monde",
  "Créé un cabinet de conseil",
  "Créé une agence marketing",
  "Créé un café concert",
  "Créé une entreprise dans l'univers de la piscine, avec plus de 1500 piscines construites dans 7 pays",
];

export default function AboutPage() {
  return (
    <>
      <UnifiedHeader />
      <EspaceProNav />
      {/* Spacer pour le header (64px) + menu nav (61px) */}
      <div style={{ height: '125px' }} />
      <div data-testid="about-page">
      {/* Hero */}
      <section className="py-20 md:py-32 gradient-mesh">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-rm-accent/30 text-rm-primary text-sm font-medium rounded-full mb-4">
                À propos
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-rm-text mb-6">
                Créé par un entrepreneur,{' '}
                <span className="text-gradient">pour les entrepreneurs</span>
              </h1>
              <p className="text-lg md:text-xl text-rm-text-secondary">
                Je suis entrepreneur depuis l'âge de 20 ans. Depuis plus de 27 ans, 
                j'ai créé et développé plusieurs entreprises dans différents secteurs.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src={FOUNDER_IMAGE}
                alt="Romain Trallero - Fondateur d'Abracadabâti"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-rm-primary/10 rounded-xl flex items-center justify-center">
                <Building className="w-7 h-7 text-rm-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-rm-text">
                Au fil des années, j'ai notamment :
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl"
                >
                  <div className="w-2 h-2 bg-rm-secondary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-rm-text-secondary">{achievement}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 p-6 bg-rm-accent/10 rounded-2xl border border-rm-accent/20">
              <p className="text-lg text-rm-text leading-relaxed">
                <strong>Entreprendre a toujours été une passion pour moi.</strong><br />
                Créer, construire, développer des projets… c'est ce que j'ai toujours aimé faire.
              </p>
              <p className="text-rm-text-secondary mt-4">
                Mais comme beaucoup d'entrepreneurs, j'ai aussi connu la réalité du terrain : 
                beaucoup de responsabilités, énormément de travail… et une entreprise qui finit 
                par dépendre entièrement de toi.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Part 1 - Dubai */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-rm-secondary/10 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-7 h-7 text-rm-secondary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-rm-text">
                Une rencontre qui va tout changer
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none space-y-6">
              <p className="text-rm-text-secondary leading-relaxed">
                En 2017, je vivais et travaillais à Dubaï. À ce moment-là, je cherchais des solutions 
                pour externaliser certaines tâches. Je devais notamment traduire des contrats officiels 
                en arabe et préparer du marketing digital en anglais — deux langues que je ne maîtrisais pas.
              </p>
              
              <p className="text-rm-text-secondary leading-relaxed">
                C'est à ce moment-là que j'ai rencontré une entrepreneuse malgache qui m'a parlé d'une 
                solution que je ne connaissais pas encore : <strong className="text-rm-text">travailler avec des talents basés à Madagascar.</strong>
              </p>
              
              <p className="text-rm-text-secondary leading-relaxed">
                J'ai décidé d'essayer. Au départ, j'ai commencé avec une seule personne. Puis trois. 
                Deux ans plus tard, <strong className="text-rm-text">plus de 40 personnes travaillaient dans mes équipes à Madagascar.</strong>
              </p>
              
              <p className="text-rm-text-secondary leading-relaxed">
                J'ai commencé à comprendre à quel point ce modèle pouvait être puissant pour les entrepreneurs.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Part 2 - Challenges */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-rm-primary/10 rounded-xl flex items-center justify-center">
                <Globe className="w-7 h-7 text-rm-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-rm-text">
                Mais gérer des équipes à distance n'est pas simple
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none space-y-6">
              <p className="text-rm-text-secondary leading-relaxed">
                Travailler avec des équipes à distance peut être extrêmement efficace. 
                Mais quand on ne connaît pas le pays, la culture ou les méthodes de travail, 
                cela peut aussi devenir très compliqué.
              </p>
              
              <p className="text-rm-text-secondary leading-relaxed">
                J'ai donc commencé à faire de nombreux allers-retours à Madagascar pour comprendre 
                le pays, rencontrer les équipes et structurer une organisation solide.
              </p>
              
              <p className="text-rm-text leading-relaxed font-medium">
                Petit à petit, j'ai appris à travailler avec Madagascar et à créer un système qui fonctionne.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Part 3 - The Fall */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-rm-text mb-8">
              Quand l'entrepreneur devient prisonnier de son entreprise
            </h2>
            
            <div className="prose prose-lg max-w-none space-y-6">
              <p className="text-rm-text-secondary leading-relaxed">
                En 2023, malgré toute mon expérience, je me suis retrouvé dans une situation que 
                beaucoup d'entrepreneurs connaissent. <strong className="text-rm-text">J'étais complètement dans l'opérationnel.</strong>
              </p>
              
              <p className="text-rm-text-secondary leading-relaxed">
                Je travaillais dans une entreprise avec une vingtaine de salariés et je gérais : 
                la partie commerciale, les chantiers, l'image sur les réseaux sociaux.
              </p>
              
              <div className="bg-white/80 rounded-xl p-6 border-l-4 border-red-400">
                <p className="text-rm-text leading-relaxed">
                  Mais je n'avais plus de recul.<br />
                  Plus le temps d'analyser.<br />
                  Plus le temps d'organiser.<br />
                  Plus le temps de réfléchir.<br /><br />
                  Et surtout, <strong>je n'avais jamais vraiment réussi à sortir de l'opérationnel.</strong><br />
                  Je n'avais pas suffisamment délégué.
                </p>
              </div>
              
              <p className="text-rm-text-secondary leading-relaxed">
                Petit à petit, mon entreprise est passée avant tout le reste. 
                Avant ma famille. Avant mes enfants. Avant le temps que j'aurais dû consacrer à ce qui comptait vraiment.
              </p>
              
              <p className="text-rm-text leading-relaxed font-medium italic">
                Aujourd'hui, avec le recul, c'est probablement l'un de mes plus grands regrets. 
                Parce que le temps est la seule chose que l'on ne peut jamais récupérer.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Part 4 - The Fall Continued */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-4xl font-bold mb-8">
              La chute
            </h2>
            
            <div className="prose prose-lg prose-invert max-w-none space-y-6 text-center">
              <p className="text-slate-300 leading-relaxed text-lg">
                À force d'être constamment dans l'opérationnel, tout a fini par s'effondrer.
              </p>
              
              <p className="text-2xl font-bold text-white">
                J'ai tout perdu.
              </p>
              
              <p className="text-slate-300 leading-relaxed">
                Mon entreprise. Mes biens. Ma stabilité.<br />
                Je me suis retrouvé tout en bas.
              </p>
              
              <p className="text-slate-400 italic">
                Une période extrêmement difficile dans ma vie.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Part 5 - The Decision */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                  <Target className="w-7 h-7 text-green-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-rm-text">
                  La décision qui va tout changer
                </h2>
              </div>
              
              <div className="prose prose-lg max-w-none space-y-6">
                <p className="text-rm-text-secondary leading-relaxed">
                  À ce moment-là, j'ai pris une décision radicale. 
                  <strong className="text-rm-text"> J'ai acheté un billet d'avion simple pour Madagascar.</strong>
                </p>
                
                <p className="text-rm-text-secondary leading-relaxed">
                  Je suis resté 10 mois sur place. Pendant ces 10 mois, j'ai reconstruit un nouveau système.
                </p>
                
                <div className="bg-white rounded-xl p-6 border border-green-200">
                  <p className="text-rm-text font-medium mb-4">Un système basé sur :</p>
                  <ul className="space-y-2 text-rm-text-secondary">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      Des assistants humains
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      Des outils d'automatisation
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      L'intelligence artificielle
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      Une organisation pensée pour les entrepreneurs
                    </li>
                  </ul>
                </div>
                
                <p className="text-rm-text leading-relaxed font-medium">
                  Un système conçu pour résoudre un problème que je connais parfaitement : 
                  les entrepreneurs qui deviennent esclaves de leur propre entreprise.
                </p>
                
                <p className="text-rm-text-secondary italic">
                  Parce que pendant longtemps, moi aussi je pensais être libre. 
                  En réalité, j'étais simplement prisonnier de mon entreprise.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={TEAM_MADAGASCAR}
                alt="L'équipe à Madagascar"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Birth of BatiAssist */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-rm-secondary/10 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-7 h-7 text-rm-secondary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-rm-text">
                La naissance d'Abracadabâti
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none space-y-6">
              <p className="text-rm-text-secondary leading-relaxed">
                C'est de cette expérience qu'est né Abracadabâti.
              </p>
              
              <p className="text-rm-text leading-relaxed text-xl font-medium">
                L'idée est simple : permettre aux entrepreneurs d'accéder à une véritable organisation 
                autour d'eux, sans devoir recruter plusieurs salariés en France et sans prendre de risques financiers.
              </p>
              
              <div className="bg-rm-primary/5 rounded-2xl p-8 border border-rm-primary/10">
                <p className="text-rm-text font-semibold mb-4">Avec Abracadabâti, un entrepreneur peut bénéficier :</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-rm-secondary/20 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-rm-secondary" />
                    </div>
                    <span className="text-rm-text-secondary">D'un(e) assistant(e) dédié(e)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-rm-secondary/20 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-rm-secondary" />
                    </div>
                    <span className="text-rm-text-secondary">D'une équipe digitale</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-rm-secondary/20 rounded-lg flex items-center justify-center">
                      <Target className="w-4 h-4 text-rm-secondary" />
                    </div>
                    <span className="text-rm-text-secondary">D'outils d'automatisation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-rm-secondary/20 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-4 h-4 text-rm-secondary" />
                    </div>
                    <span className="text-rm-text-secondary">D'un système pour développer son activité</span>
                  </div>
                </div>
                <p className="text-rm-text-secondary mt-6">
                  Le tout avec une organisation pensée pour les entrepreneurs et les artisans.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Personal Mission */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center">
                <Heart className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-rm-text">
                Une mission personnelle
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none space-y-6">
              <p className="text-rm-text-secondary leading-relaxed">
                Aujourd'hui, je développe ce projet avec un partenaire suisse, et nous accompagnons 
                déjà des entrepreneurs en France, en Suisse et en Belgique.
              </p>
              
              <p className="text-rm-text leading-relaxed font-medium">
                Mais au-delà du projet entrepreneurial, il y a aussi une mission personnelle.
              </p>
              
              <p className="text-rm-text-secondary leading-relaxed">
                Après avoir connu la pression, les difficultés et la chute, je veux aider d'autres 
                entrepreneurs à ne pas vivre ce que j'ai vécu.
              </p>
              
              <p className="text-rm-text-secondary leading-relaxed">
                Les aider à construire une entreprise qui fonctionne <strong className="text-rm-text">avec eux</strong>, 
                mais pas <strong className="text-rm-text">uniquement grâce à eux</strong>.
              </p>
              
              <div className="bg-white rounded-2xl p-8 border border-slate-200">
                <p className="text-rm-text font-semibold mb-4">Une entreprise qui leur permet de :</p>
                <ul className="space-y-3 text-rm-text-secondary">
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                    </div>
                    Développer leur activité
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                    </div>
                    Gagner du temps
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                    </div>
                    Réduire la pression
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                    </div>
                    Et surtout retrouver du temps pour ce qui compte vraiment
                  </li>
                </ul>
              </div>
              
              <p className="text-xl text-rm-text font-semibold text-center pt-4">
                C'est exactement la mission d'Abracadabâti.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-rm-primary/5 to-rm-accent/10">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
              Prêt à transformer ton organisation ?
            </h2>
            <p className="text-lg md:text-xl text-rm-text-secondary mb-8">
              Réserve un entretien pour découvrir comment Abracadabâti 
              peut t'aider à reprendre le contrôle de ton temps.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                Réserver un entretien avec Romain
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
    
    </>
  );
}
